import { lazy, Suspense, useLayoutEffect, useState } from "react";
import {
  Activity, Calculator, RefreshCw, TrendingDown,
  Calendar, Beef, AlertCircle, DollarSign, MousePointerClick
} from "lucide-react";
import { CALCULATOR_SLUGS, trackEvent, trackPageView } from "../services/analytics";
import { selectClinicalBranch, selectClosestDose } from "../utils/clinicalReferenceSelect";
import type { Gyogyszer, ClinicalPopulation } from "../data/clinicalReference";
import { IS_NATIVE, isToolVisible, NATIVE_DEFAULT_TOOL } from "../config/platform";
import { useLastLoggedDose } from "../hooks/useLastLoggedDose";

// ─── Types ───────────────────────────────────────────────────────────
export type ToolId =
  | "plotter"
  | "kalkulator"
  | "microdose"
  | "predikator"
  | "titralo"
  | "feherje"
  | "kimaradt"
  | "ar"
  | "kattintas";

interface Tool {
  id: ToolId;
  label: string;
  icon: React.ElementType;
  color: string;
}

// ─── Tool komponensek ─────────────────────────────────────────────────

function Predikator() {
  const [currentWeight, setCurrentWeight] = useState("");
  const [drug, setDrug] = useState<Gyogyszer>("tirzepatide");
  const [population, setPopulation] = useState<ClinicalPopulation>("testsulycsokkenes");
  const [dose, setDose] = useState("10");

  // Natívon nincs mg-választó (App Store 1.4.2 / APL531603): a becslés a
  // felhasználó SAJÁT, már naplózott dózisából indul. Weben a választó marad,
  // és a hook egyetlen Firestore-olvasást sem indít.
  const { doseMg: loggedDoseMg, loading: doseLoading } = useLastLoggedDose(IS_NATIVE);

  // docs/KLINIKAI_ADATOK.md — egyetlen forrás: src/data/clinicalReference.ts.
  const branch = selectClinicalBranch(drug, population);
  const doseOptions = branch?.doses ?? [];

  // Natívon a naplózott dózishoz legközelebbi vizsgálati kar; ha nincs
  // naplózott dózis, NINCS becslés (üres állapot) — csendes default mg soha
  // (docs/FOGALMAK.md #9).
  const nativeMatchedDose =
    IS_NATIVE && branch && loggedDoseMg !== null ? selectClosestDose(branch, loggedDoseMg) : null;

  const w = parseFloat(currentWeight);
  const pct = IS_NATIVE
    ? nativeMatchedDose?.pctChange
    : doseOptions.find((d) => String(d.doseMg) === dose)?.pctChange;
  const predictedLoss = pct && w ? (w * Math.abs(pct)) / 100 : null;
  const predictedWeight = predictedLoss ? w - predictedLoss : null;
  const weeks = branch?.weeks ?? 72;

  // Heti bontás — a forrás csak a végpontot közli, a köztes pontok
  // szintetikus közelítő (exponenciális) képlettel becsültek, NEM
  // publikált adatpontok (docs/KLINIKAI_ADATOK.md 5. szakasz).
  const weeklyData = pct
    ? [0, weeks / 6, weeks / 3, weeks / 2, (weeks * 2) / 3, weeks].map((week) => ({
        week: Math.round(week),
        weight: w
          ? (w + (w * pct) / 100 / weeks * week * (1 - Math.exp(-week / 20))).toFixed(1)
          : null,
      }))
    : [];

  const handleDrugChange = (next: Gyogyszer) => {
    setDrug(next);
    const nextBranch = selectClinicalBranch(next, population);
    setDose(String(nextBranch?.doses[nextBranch.doses.length - 1]?.doseMg ?? ""));
  };
  const handlePopulationChange = (next: ClinicalPopulation) => {
    setPopulation(next);
    const nextBranch = selectClinicalBranch(drug, next);
    setDose(String(nextBranch?.doses[nextBranch.doses.length - 1]?.doseMg ?? ""));
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {IS_NATIVE
          ? "A saját adataid alapján, klinikai vizsgálati adatokhoz mérve becsüli a várható fogyást."
          : "Klinikai vizsgálati adatok alapján becsüli a várható fogyást."}
      </p>

      {/* Natív üres állapot: naplózott dózis nélkül nincs mihez mérni.
          Csendes default mg SOHA (docs/FOGALMAK.md #9). */}
      {IS_NATIVE && !doseLoading && loggedDoseMg === null && (
        <div className="bg-gray-50 rounded-xl p-3">
          <p className="text-xs text-gray-600">
            A becsléshez naplózz egy injekciót — a számítás a saját, rögzített adataidból indul.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Jelenlegi súly (kg)</label>
          <input
            type="number"
            value={currentWeight}
            onChange={(e) => setCurrentWeight(e.target.value)}
            placeholder="pl. 110"
            className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Cél</label>
          <select
            value={population}
            onChange={(e) => handlePopulationChange(e.target.value as ClinicalPopulation)}
            className="w-full border border-gray-200 rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          >
            <option value="testsulycsokkenes">Testsúlycsökkentés</option>
            <option value="cukorbetegseg">Cukorbetegség</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Gyógyszer</label>
            <select
              value={drug}
              onChange={(e) => handleDrugChange(e.target.value as Gyogyszer)}
              className="w-full border border-gray-200 rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="tirzepatide">Tirzepatide</option>
              <option value="semaglutide">Semaglutide</option>
            </select>
          </div>
          {!IS_NATIVE && (
            <div>
              <label className="text-xs text-gray-500 block mb-1">Dózis</label>
              <select
                value={dose}
                onChange={(e) => setDose(e.target.value)}
                className="w-full border border-gray-200 rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                {doseOptions.map((d) => (
                  <option key={d.doseMg} value={d.doseMg}>{d.doseMg} mg</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {predictedWeight && pct && branch && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500">Várható fogyás ({weeks} hét)</p>
              <p className="text-2xl font-bold text-emerald-700">-{predictedLoss?.toFixed(1)} kg</p>
              <p className="text-xs text-emerald-600">({pct}%)</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <p className="text-xs text-gray-500">Várható célsúly</p>
              <p className="text-2xl font-bold text-blue-700">{predictedWeight.toFixed(1)} kg</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-600">Várható haladás hetek szerint:</p>
            {weeklyData.map(({ week, weight }) => (
              <div key={week} className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-xl text-sm">
                <span className="text-gray-500">{week}. hét</span>
                <span className="font-semibold text-gray-800">{weight ? `${weight} kg` : "–"}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400">
            {branch.trialName} vizsgálat · {branch.weeks} hét · {branch.populationLabel}.
            {branch.derived && " A dózisonkénti érték a vizsgálat abszolút adataiból származtatott közelítés."}
            {" "}A végpont ({weeks}. hét) a publikált adat, a köztes heti bontás szintetikus közelítő becslés, nem publikált mérés.
          </p>
        </>
      )}

      <p className="text-xs text-gray-400 italic">
        Átlagos klinikai eredmény, egyéni variabilitás jelentős lehet. Nem orvosi tanács.
      </p>
    </div>
  );
}

function Feherje() {
  const [weight, setWeight] = useState("");
  const [activity, setActivity] = useState<"low" | "medium" | "high">("medium");
  const [goal, setGoal] = useState<"loss" | "maintain">("loss");

  const w = parseFloat(weight);
  const multipliers = {
    low: { loss: 1.6, maintain: 1.4 },
    medium: { loss: 1.8, maintain: 1.6 },
    high: { loss: 2.2, maintain: 2.0 },
  };
  const multiplier = multipliers[activity][goal];
  const dailyProtein = w ? Math.round(w * multiplier) : null;

  const foods = [
    { name: "Csirkemell (100g)", protein: 31 },
    { name: "Tojás (1 db)", protein: 6 },
    { name: "Görög joghurt (150g)", protein: 15 },
    { name: "Tonhal (100g)", protein: 26 },
    { name: "Túró (100g)", protein: 12 },
    { name: "Lencse főtt (100g)", protein: 9 },
  ];

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        GLP-1 kezelés során az izomvesztés megelőzéséhez kiemelt fontosságú a megfelelő fehérjebevitel.
      </p>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Testsúly (kg)</label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="pl. 95"
            className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Aktivitási szint</label>
          <div className="grid grid-cols-3 gap-2">
            {(["low", "medium", "high"] as const).map((a) => (
              <button
                key={a}
                onClick={() => setActivity(a)}
                className={`py-2 rounded-xl text-xs font-medium border transition-all ${
                  activity === a
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {a === "low" ? "Alacsony" : a === "medium" ? "Közepes" : "Magas"}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Cél</label>
          <div className="grid grid-cols-2 gap-2">
            {(["loss", "maintain"] as const).map((g) => (
              <button
                key={g}
                onClick={() => setGoal(g)}
                className={`py-2 rounded-xl text-sm font-medium border transition-all ${
                  goal === g
                    ? "bg-emerald-700 text-white border-emerald-700"
                    : "bg-white text-gray-600 border-gray-200"
                }`}
              >
                {g === "loss" ? "Fogyás" : "Fenntartás"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {dailyProtein && (
        <>
          <div className="bg-emerald-50 rounded-xl p-4 text-center">
            <p className="text-xs text-gray-500 mb-1">Napi fehérje cél</p>
            <p className="text-3xl font-bold text-emerald-700">{dailyProtein} g</p>
            <p className="text-xs text-emerald-600 mt-1">{multiplier} g/kg ({activity === "low" ? "alacsony" : activity === "medium" ? "közepes" : "magas"} aktivitás)</p>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Fehérjedús ételek referencia:</p>
            <div className="space-y-1.5">
              {foods.map(({ name, protein }) => (
                <div key={name} className="flex justify-between items-center px-3 py-2 bg-gray-50 rounded-xl text-sm">
                  <span className="text-gray-600">{name}</span>
                  <span className="font-semibold text-gray-800">{protein}g fehérje</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ArKalkulator() {
  const [drug, setDrug] = useState("tirzepatide");
  const [monthlyPrice, setMonthlyPrice] = useState("");
  const [startWeight, setStartWeight] = useState("");
  const [currentWeight, setCurrentWeight] = useState("");
  const [months, setMonths] = useState("");

  const price = parseFloat(monthlyPrice);
  const sw = parseFloat(startWeight);
  const cw = parseFloat(currentWeight);
  const m = parseFloat(months);

  const totalCost = price && m ? price * m : null;
  const lostKg = sw && cw ? sw - cw : null;
  const costPerKg = totalCost && lostKg && lostKg > 0 ? totalCost / lostKg : null;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Számítsd ki mennyi fogyás jutott egy forintra — segít átlátni a kezelés hatékonyságát.
      </p>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Gyógyszer</label>
            <select
              value={drug}
              onChange={(e) => setDrug(e.target.value)}
              className="w-full border border-gray-200 rounded-xl p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            >
              <option value="tirzepatide">Tirzepatide (Mounjaro)</option>
              <option value="semaglutide">Semaglutide (Ozempic)</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Havi ár (Ft)</label>
            <input
              type="number"
              value={monthlyPrice}
              onChange={(e) => setMonthlyPrice(e.target.value)}
              placeholder="pl. 60000"
              className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Kezdő súly (kg)</label>
            <input
              type="number"
              value={startWeight}
              onChange={(e) => setStartWeight(e.target.value)}
              placeholder="pl. 120"
              className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Jelenlegi (kg)</label>
            <input
              type="number"
              value={currentWeight}
              onChange={(e) => setCurrentWeight(e.target.value)}
              placeholder="pl. 100"
              className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Hónapok száma</label>
            <input
              type="number"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              placeholder="pl. 6"
              className="w-full border border-gray-200 rounded-xl p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            />
          </div>
        </div>
      </div>

      {totalCost && lostKg && lostKg > 0 && costPerKg && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Összes ktg.</p>
            <p className="text-base font-bold text-gray-800">
              {totalCost.toLocaleString("hu-HU")} Ft
            </p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Fogyás</p>
            <p className="text-base font-bold text-emerald-700">-{lostKg.toFixed(1)} kg</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center">
            <p className="text-xs text-gray-500">Ft / kg</p>
            <p className="text-base font-bold text-blue-700">
              {Math.round(costPerKg).toLocaleString("hu-HU")}
            </p>
          </div>
        </div>
      )}

      {lostKg !== null && lostKg <= 0 && (
        <p className="text-xs text-amber-600 bg-amber-50 rounded-xl p-3">
          A jelenlegi súly nagyobb vagy egyenlő a kezdő súllyal.
        </p>
      )}
    </div>
  );
}

// ─── Fő Eszközök oldal ───────────────────────────────────────────────

// ⚠️ A4 (külső audit, 2026-10-14): a natívon REJTETT eszközök (lásd
// NATIVE_HIDDEN_TOOLS a config/platform.ts-ben) NEM kerülhetnek a natív
// bundle-be — sem a kódjuk, sem a szövegük, sem a menücímkéjük. Korábban csak
// futásidőben voltak elrejtve, és a becsomagolt iOS main.*.js szó szerint
// tartalmazta őket ("nincsenek jelen" — ez nem volt igaz).
//
// A megoldás FORDÍTÁSI idejű: a `process.env.REACT_APP_BUILD_MODE` a build.sh-ból
// jön, a CRA szó szerinti stringre cseréli, így a feltétel fordításkor
// kiértékelhető, és a webpack a holt ág `import()`-ját és literáljait ki sem
// bocsátja. ⚠️ EZÉRT A FELTÉTELNEK ITT, SZÓ SZERINT KELL ÁLLNIA — egy másik
// modulból importált konstans mögött a holt-ág-elhagyás nem garantált.
// Ellenőrzés: scripts/check-native-build.sh (a build.sh --native futtatja).
// A futásidejű IS_NATIVE-kapu lent második védelemként megmarad.
const WEB_ONLY_TOOLS: Tool[] =
  process.env.REACT_APP_BUILD_MODE === "native"
    ? []
    : [
        { id: "plotter", label: "GLP Plotter", icon: Activity, color: "text-emerald-700" },
        { id: "kalkulator", label: "Dózis kalkulátor", icon: Calculator, color: "text-blue-700" },
        { id: "microdose", label: "Microdose", icon: RefreshCw, color: "text-violet-700" },
        { id: "titralo", label: "Titráló tervező", icon: Calendar, color: "text-amber-700" },
        { id: "kimaradt", label: "Kimaradt injekció", icon: AlertCircle, color: "text-red-700" },
        { id: "kattintas", label: "Kattintás kalkulátor", icon: MousePointerClick, color: "text-indigo-700" },
      ];

/** Egy lazy-betöltött eszköz elemként. A hívások a lenti feltételes kifejezés
 * WEBES ágában állnak — natív buildben ez az ág holt, az import()-ok nem
 * kerülnek a bundle-be (és chunk sem keletkezik belőlük). */
function lazyTool(load: () => Promise<{ default: React.ComponentType }>): React.ReactNode {
  const Tool = lazy(load);
  return <Tool />;
}

const WEB_ONLY_COMPONENTS: Partial<Record<ToolId, React.ReactNode>> =
  process.env.REACT_APP_BUILD_MODE === "native"
    ? {}
    : {
        plotter: lazyTool(() => import("./eszkozok/GlpPlotter")),
        kalkulator: lazyTool(() => import("./eszkozok/Kalkulator")),
        microdose: lazyTool(() => import("./eszkozok/Microdose")),
        titralo: lazyTool(() => import("./eszkozok/Titralo")),
        kimaradt: lazyTool(() => import("./eszkozok/Kimaradt")),
        kattintas: lazyTool(() => import("./eszkozok/KattintasKalkulator")),
      };

const COMMON_TOOLS: Tool[] = [
  { id: "predikator", label: "Súly predikátor", icon: TrendingDown, color: "text-rose-700" },
  { id: "feherje", label: "Fehérje kalkulátor", icon: Beef, color: "text-orange-700" },
  { id: "ar", label: "Ár vs fogyás", icon: DollarSign, color: "text-teal-700" },
];

/** A menü sorrendje — a webes buildben ugyanaz, mint korábban. */
const TOOL_ORDER: ToolId[] = [
  "plotter", "kalkulator", "microdose", "predikator", "titralo",
  "feherje", "kimaradt", "ar", "kattintas",
];

const tools: Tool[] = TOOL_ORDER
  .map((id) => [...WEB_ONLY_TOOLS, ...COMMON_TOOLS].find((t) => t.id === id))
  .filter((t): t is Tool => t !== undefined);

const toolComponents: Partial<Record<ToolId, React.ReactNode>> = {
  ...WEB_ONLY_COMPONENTS,
  predikator: <Predikator />,
  feherje: <Feherje />,
  ar: <ArKalkulator />,
};

export default function Eszkozok() {
  // Platform-kapu: natívon a dózis-számoló eszközök nem léteznek (App Store
  // 1.4.2 / APL531603) — nem „letiltva", hanem nincsenek a listában, így se
  // menüpontként, se aktiválható panelként nem érhetők el. A kapu egyetlen
  // forrása: src/config/platform.ts.
  const visibleTools = tools.filter((t) => isToolVisible(t.id, IS_NATIVE));
  const defaultTool: ToolId = IS_NATIVE ? NATIVE_DEFAULT_TOOL : "plotter";

  const [active, setActive] = useState<ToolId>(defaultTool);

  // Védőháló: ha bármi mégis egy natívon rejtett eszközre állítaná az
  // aktív állapotot, essünk vissza az alapértelmezettre — a rejtett eszköz
  // panelje soha ne renderelődhessen natívon.
  const safeActive = isToolVisible(active, IS_NATIVE) ? active : defaultTool;
  const activeTool = visibleTools.find((t) => t.id === safeActive)!;

  // Egy title/page_view + calculator_opened esemény aktív-eszköz váltásonként
  // (az eszközön belüli mezőváltozások — dózis, hét stb. — nem tüzelnek újra).
  useLayoutEffect(() => {
    const title = `${activeTool.label} – GLP Napló`;
    document.title = title;
    trackPageView("/eszkozok", title);
    trackEvent("calculator_opened", { calculator_name: CALCULATOR_SLUGS[safeActive] });
  }, [safeActive, activeTool.label]);

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 pb-6">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Eszközök</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          {IS_NATIVE ? "Segédeszközök a terápiád követéséhez" : "GLP-1 kalkulátorok és útmutatók"}
        </p>
      </div>

      {/* Tool grid */}
      <div className="grid grid-cols-4 gap-2 mb-6">
        {visibleTools.map(({ id, label, icon: Icon, color }) => (
          <button
            key={id}
            onClick={() => setActive(id)}
            className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border transition-all text-center ${
              safeActive === id
                ? "bg-emerald-700 border-emerald-700 text-white shadow-md"
                : "bg-white border-gray-100 text-gray-600 hover:border-emerald-200"
            }`}
          >
            <Icon size={18} className={safeActive === id ? "text-white" : color} />
            <span className="text-[10px] leading-tight font-medium">{label}</span>
          </button>
        ))}
      </div>

      {/* Active tool panel */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
          <activeTool.icon size={18} className={activeTool.color} />
          <h2 className="font-semibold text-gray-800">{activeTool.label}</h2>
        </div>
        {/* A webes buildben a rejtett eszközök lazy chunkból jönnek (A4). */}
        <Suspense fallback={<div className="py-10 text-center text-sm text-gray-400">Betöltés…</div>}>
          {toolComponents[safeActive]}
        </Suspense>
      </div>

      {/* Natívon semleges tájékoztató — kerülőút-keretezés nélkül. */}
      {IS_NATIVE && (
        <p className="text-xs text-gray-400 text-center mt-4">
          A teljes eszköztár a glpnaplo.hu-n érhető el.
        </p>
      )}
    </div>
  );
}
